import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/app";
import { stringValue } from "./firestore";

export interface CountryResource {
  officialNameEN: string;
  isoAlpha2: string;
  isoAlpha3: string;
  iocCode: string;
  fifaCode: string;
  flagURL?: string;
  regionName?: string;
  continentCode?: string;
}

export function parseCountryResourcesDocument(data: Record<string, unknown> | undefined): CountryResource[] {
  const rawCountries = Array.isArray(data?.countries) ? data.countries : [];
  return rawCountries
    .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === "object")
    .flatMap((entry) => {
      const officialNameEN = stringValue(entry["official-name-en"])?.trim();
      if (!officialNameEN) return [];
      const flagURL = usableFlagURL(stringValue(entry["flag-url"]));
      return [
        {
          officialNameEN,
          isoAlpha2: stringValue(entry["iso-3166-1-alpha-2"]) ?? "",
          isoAlpha3: stringValue(entry["iso-3166-1-alpha-3"]) ?? "",
          iocCode: stringValue(entry["ioc-code"]) ?? "",
          fifaCode: stringValue(entry["fifa-code"]) ?? "",
          ...(flagURL ? { flagURL } : {}),
          ...optionalStringProperty("regionName", stringValue(entry["region-name"])),
          ...optionalStringProperty("continentCode", stringValue(entry["continent-code"])),
        },
      ];
    })
    .sort((a, b) => a.officialNameEN.localeCompare(b.officialNameEN, undefined, { sensitivity: "base" }));
}

export async function fetchCountryResources(): Promise<CountryResource[]> {
  const snapshot = await getDoc(doc(db, "Utilities", "CountryResources"));
  return parseCountryResourcesDocument(snapshot.data());
}

export function preferredBroadcastCode(country: CountryResource): string | undefined {
  return country.iocCode.trim() || country.isoAlpha3.trim() || undefined;
}

export function preferredBroadcastCodeSystem(country: CountryResource): string | undefined {
  if (country.iocCode.trim()) return "IOC";
  if (country.isoAlpha3.trim()) return "ISO-3166-1-alpha-3";
  return undefined;
}

export function countryResourceMatching(countryName: string, countries: CountryResource[]): CountryResource | undefined {
  const normalized = normalizeCountryName(countryName);
  if (!normalized) return undefined;
  const byName = countries.find((country) => normalizeCountryName(country.officialNameEN) === normalized);
  return byName ?? countryResourceMatchingCode(countryName, countries);
}

// External feeds often carry country codes ("DEU", "de") where a name is
// expected. Only attempted after name matching fails, and only for values
// shaped like a code, so official names are never shadowed by a code.
function countryResourceMatchingCode(value: string, countries: CountryResource[]): CountryResource | undefined {
  const code = value.trim().toUpperCase();
  if (!/^[A-Z]{2,3}$/.test(code)) return undefined;
  for (const key of ["isoAlpha3", "iocCode", "fifaCode", "isoAlpha2"] as const) {
    const match = countries.find((country) => country[key].trim().toUpperCase() === code);
    if (match) return match;
  }
  return undefined;
}

function optionalStringProperty<K extends string>(key: K, value: string | undefined): Partial<Record<K, string>> {
  const trimmed = value?.trim();
  return trimmed ? { [key]: trimmed } as Record<K, string> : {};
}

function usableFlagURL(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed !== "--" ? trimmed : undefined;
}

function normalizeCountryName(value: string): string {
  return value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}
